// Renekton Dominus — Renekton erupts in furious rage, then fire tendrils
// spread to each enemy unit. Totally different from Garen's spin:
// this is an outward explosion that crawls across to every target.

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";

const FURY_MS   = 300;   // eruption on caster
const SPREAD_MS = 380;   // tendrils reach enemies
const BURN_MS   = 360;   // burn flash at each enemy + fade
const TOTAL     = FURY_MS + SPREAD_MS + BURN_MS;

type Tendril = {
  ctrl: Point;    // bezier control point (arcs upward)
  end:  Point;    // enemy position
  seed: number;   // for tendril jag variations
};

function buildTendrils(from: Point, targets: Point[]): Tendril[] {
  return targets.map((to, i) => {
    const ctrl = {
      x: (from.x + to.x) / 2 + (Math.sin(i * 2.3) * 28),
      y: (from.y + to.y) / 2 - 38 - i * 6,
    };
    return { ctrl, end: to, seed: i * 1.7 + 0.4 };
  });
}

// Points along a quadratic bezier
function bezierPoint(from: Point, ctrl: Point, to: Point, t: number): Point {
  const u = 1 - t;
  return {
    x: u * u * from.x + 2 * u * t * ctrl.x + t * t * to.x,
    y: u * u * from.y + 2 * u * t * ctrl.y + t * t * to.y,
  };
}

export class RenektonFuryVFX implements Animation {
  private elapsed  = 0;
  private tendrils: Tendril[];

  constructor(
    private from:    Point,
    private targets: Point[],
    private radius:  number,
    private onSpread?: () => void,
  ) {
    this.tendrils = buildTendrils(from, targets);
  }

  update(dt: number): void {
    const prev = this.elapsed;
    this.elapsed = Math.min(this.elapsed + dt, TOTAL);
    if (prev < FURY_MS + SPREAD_MS && this.elapsed >= FURY_MS + SPREAD_MS) {
      this.onSpread?.();
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const e = this.elapsed;

    if (e < FURY_MS) {
      this.drawFury(ctx, e / FURY_MS);
    } else if (e < FURY_MS + SPREAD_MS) {
      this.drawFury(ctx, 1);
      this.drawSpread(ctx, (e - FURY_MS) / SPREAD_MS);
    } else {
      const bt = (e - FURY_MS - SPREAD_MS) / BURN_MS;
      this.drawFury(ctx, 1 - bt);
      this.drawBurn(ctx, bt);
    }
  }

  private drawFury(ctx: CanvasRenderingContext2D, t: number): void {
    const fade = t;
    const cx = this.from.x;
    const cy = this.from.y;

    ctx.save();

    // Three expanding fury rings
    for (let ring = 0; ring < 3; ring++) {
      const delay = ring * 0.2;
      const rt = Math.max(0, (t - delay) / (1 - delay));
      const r = this.radius * (0.5 + ring * 0.6) * Easing.easeOutQuad(rt);
      if (rt <= 0) continue;
      ctx.globalAlpha = (1 - rt) * 0.7 * (1 - ring * 0.2);
      ctx.strokeStyle = ring === 0 ? "#ff5500" : ring === 1 ? "#dd3300" : "#bb2200";
      ctx.lineWidth   = 3 - ring * 0.5;
      ctx.shadowColor = "#cc2200";
      ctx.shadowBlur  = 16;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Scale/croc pattern: 8 overlapping arcs creating scale texture
    if (t > 0.25) {
      const scaleT = (t - 0.25) / 0.75;
      const scaleR = this.radius * 0.55 * scaleT;
      ctx.globalAlpha = fade * 0.5;
      ctx.strokeStyle = "#ff6600";
      ctx.lineWidth   = 2;
      ctx.shadowColor = "#aa2200";
      ctx.shadowBlur  = 8;
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        const sx = cx + Math.cos(a) * scaleR;
        const sy = cy + Math.sin(a) * scaleR;
        ctx.beginPath();
        ctx.arc(sx, sy, scaleR * 0.42, 0, Math.PI, false);
        ctx.stroke();
      }
    }

    // Rage aura fill
    const furyGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, this.radius * 0.9 * t);
    furyGrad.addColorStop(0, "rgba(255,60,0,0.35)");
    furyGrad.addColorStop(0.5, "rgba(180,30,0,0.18)");
    furyGrad.addColorStop(1, "rgba(100,10,0,0)");
    ctx.globalAlpha = fade * 0.6;
    ctx.fillStyle   = furyGrad;
    ctx.shadowBlur  = 0;
    ctx.beginPath();
    ctx.arc(cx, cy, this.radius * 0.9 * t, 0, Math.PI * 2);
    ctx.fill();

    // Central burst embers
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2 + t * 1.8;
      const dist = this.radius * 0.6 * t;
      const ex = cx + Math.cos(a) * dist;
      const ey = cy + Math.sin(a) * dist;
      ctx.globalAlpha = fade * 0.6;
      ctx.fillStyle   = i % 2 === 0 ? "#ff6600" : "#ffaa33";
      ctx.shadowColor = "#ff4400";
      ctx.shadowBlur  = 8;
      ctx.beginPath();
      ctx.arc(ex, ey, 2.5 + (i % 3), 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  private drawSpread(ctx: CanvasRenderingContext2D, t: number): void {
    ctx.save();

    for (let i = 0; i < this.tendrils.length; i++) {
      const td = this.tendrils[i];
      // Stagger each tendril slightly
      const startT = i * (0.12 / Math.max(this.tendrils.length, 1));
      const local  = Math.max(0, (t - startT) / (1 - startT));
      if (local <= 0) continue;

      const drawEnd = Easing.easeOutQuad(local);

      // Draw tendril as multiple bezier samples
      const STEPS = 18;
      const GLOW_PASSES = 2;
      for (let pass = 0; pass < GLOW_PASSES; pass++) {
        ctx.globalAlpha = pass === 0 ? 0.35 : 0.8;
        ctx.strokeStyle = pass === 0 ? "#ff4400" : "#ffaa33";
        ctx.lineWidth   = pass === 0 ? 8 : 2.5;
        ctx.lineCap     = "round";
        ctx.lineJoin    = "round";
        ctx.shadowColor = "#cc2200";
        ctx.shadowBlur  = pass === 0 ? 16 : 8;

        ctx.beginPath();
        let started = false;
        for (let j = 0; j <= Math.floor(STEPS * drawEnd); j++) {
          const tSeg = j / STEPS;
          const pt   = bezierPoint(this.from, td.ctrl, td.end, tSeg);
          // Jag: small lateral offset based on sin (crackle effect)
          const jag = Math.sin(tSeg * Math.PI * 6 + td.seed) * 3 * (1 - tSeg);
          const perpX = -(td.end.y - this.from.y) / Math.hypot(td.end.x - this.from.x, td.end.y - this.from.y) * jag;
          const perpY =  (td.end.x - this.from.x) / Math.hypot(td.end.x - this.from.x, td.end.y - this.from.y) * jag;
          if (!started) { ctx.moveTo(pt.x + perpX, pt.y + perpY); started = true; }
          else ctx.lineTo(pt.x + perpX, pt.y + perpY);
        }
        ctx.stroke();
      }

      // Leading ember at the tip
      const tipT = drawEnd;
      const tip  = bezierPoint(this.from, td.ctrl, td.end, tipT);
      ctx.globalAlpha = 0.9;
      ctx.fillStyle   = "#ffffff";
      ctx.shadowColor = "#ff8800";
      ctx.shadowBlur  = 18;
      ctx.beginPath();
      ctx.arc(tip.x, tip.y, 5, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  private drawBurn(ctx: CanvasRenderingContext2D, t: number): void {
    const fade = 1 - Easing.easeInQuad(t);

    ctx.save();

    for (const td of this.tendrils) {
      const x = td.end.x;
      const y = td.end.y;

      // Burn ring
      ctx.globalAlpha = fade * 0.75;
      ctx.strokeStyle = "#ff4400";
      ctx.lineWidth   = 2.5;
      ctx.shadowColor = "#cc2200";
      ctx.shadowBlur  = 14;
      ctx.beginPath();
      ctx.arc(x, y, 8 + t * 22, 0, Math.PI * 2);
      ctx.stroke();

      // Ember sparks
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2 + t * 0.8;
        const d = 6 + t * 16;
        ctx.globalAlpha = fade * 0.6;
        ctx.fillStyle   = i % 2 === 0 ? "#ffaa33" : "#ff4400";
        ctx.shadowBlur  = 6;
        ctx.beginPath();
        ctx.arc(x + Math.cos(a) * d, y + Math.sin(a) * d, 2, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= TOTAL;
  }
}
